import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowUpRight } from "lucide-react";
import { RevealLines, Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES, PAGE_META } from "@/data/content";
import usePageMeta from "@/hooks/usePageMeta";

function FadeSlideshow({ images, className = "" }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % images.length), 3800);
    return () => clearInterval(id);
  }, [images.length]);
  return (
    <div className={`relative overflow-hidden border border-y5-line ${className}`}>
      <AnimatePresence>
        <motion.img
          key={i}
          src={images[i]}
          alt="Yellow5"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute bottom-4 left-4 flex gap-1.5 z-10">
        {images.map((_, idx) => (
          <span key={idx} className={`h-1 transition-all duration-500 ${idx === i ? "w-6 bg-y5-yellow" : "w-2 bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}

export default function About() {
  const { t, lang } = useLanguage();
  usePageMeta({ ...PAGE_META[lang].about, lang, image: IMAGES.gallery[0] });
  const a = t.about;

  return (
    <div className="pt-32 pb-28 min-h-screen">
      {/* Intro */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{a.kicker}</span>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight uppercase mt-4 mb-10 md:mb-16 leading-[0.95]">
          <RevealLines lines={a.title} />
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-32">
          <Reveal className="lg:col-span-7 space-y-6">
            {a.paragraphs.map((p, i) => (<p key={i} className="text-white/80 text-lg leading-relaxed">{p}</p>))}
            <div className="flex flex-wrap gap-8 pt-6">
              {a.stats.map((s) => (
                <div key={s.l}>
                  <div className="font-display text-4xl text-y5-yellow tracking-tight">{s.k}</div>
                  <div className="text-y5-muted text-xs uppercase tracking-widest mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <FadeSlideshow images={IMAGES.gallery} className="h-[420px]" />
          </Reveal>
        </div>
      </div>

      {/* People gallery */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 mb-32">
        <h2 className="font-display text-4xl md:text-5xl tracking-tight uppercase mb-10">{a.galleryTitle}</h2>
        <FadeSlideshow images={[...IMAGES.gallery].reverse()} className="h-72 md:h-[460px]" />
      </div>

      {/* DNA */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{a.dna.kicker}</span>
            <h2 className="font-display text-5xl md:text-6xl tracking-tight uppercase mt-4 leading-[0.95]">{a.dna.title}</h2>
          </div>
          <Reveal className="lg:col-span-8 space-y-6">
            {a.dna.paragraphs.map((p, i) => (<p key={i} className="text-white/80 text-lg leading-relaxed">{p}</p>))}
          </Reveal>
        </div>
      </div>

      {/* Mission & Values */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 mb-32">
        <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{a.mission.kicker}</span>
        <h2 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight uppercase mt-4 mb-6">{a.mission.title}</h2>
        <p className="text-white/80 text-lg max-w-3xl mb-14">{a.mission.text}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-y5-line border border-y5-line">
          {a.mission.values.map((v, i) => (
            <Reveal key={v.n} delay={i * 0.08} className="bg-y5-ink p-8 hover:bg-y5-surface transition-colors">
              <div className="font-display text-5xl text-stroke mb-6">{v.n}</div>
              <h3 className="font-display text-2xl uppercase tracking-tight mb-3">{v.t}</h3>
              <p className="text-y5-muted leading-relaxed text-sm">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Experts network */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{a.experts.kicker}</span>
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight uppercase mt-4">
              {a.experts.title.map((l, i) => (<span key={i} className="block">{l}</span>))}
            </h2>
          </div>
          <p className="text-y5-muted max-w-sm">{a.experts.desc}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {a.experts.list.map((e, i) => (
            <Reveal key={e.name} delay={i * 0.06} className="border border-y5-line bg-y5-surface p-8 hover:border-y5-yellow transition-colors">
              <Quote className="text-y5-yellow mb-5" size={28} />
              <p className="text-white/80 leading-relaxed mb-6">{e.quote}</p>
              <div className="pt-4 border-t border-y5-line">
                <p className="font-display text-xl uppercase tracking-tight">{e.name}</p>
                <p className="text-y5-yellow text-xs uppercase tracking-widest mt-1">{e.role}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 border border-y5-line p-10 bg-y5-surface">
          <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{a.experts.joinTitle}</h3>
          <Link to="/candidature" data-testid="about-cta" className="inline-flex items-center gap-2 bg-y5-yellow text-y5-ink px-8 py-4 font-semibold hover:scale-[1.03] transition-transform shrink-0">
            {a.experts.joinCta} <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}
